import React from 'react';
import { translate } from 'react-i18next';
import moment from 'moment';
import CBInputField from '../../shared/form_components/CBInputField';
import CBTextAreaField from '../../shared/form_components/CBTextAreaField';
import CBDatePicker from '../../shared/CBDatePicker';
import CBLabelAndCheckbox from '../../shared/CBLabelAndCheckbox';
import CBButtonSettings from '../../shared/CBButtonSettings';
import CBPromptModal from '../../shared/CBPromptModal';

function formDataFrom(agreement) {
  if (!agreement) {
    return {
      leasing_business_id: '',
      end_date: null,
      never_ends: false,
      registration_numbers: '',
      note: '',
      new_attachment: null,
      attachment_name: '',
      remove_attachment: false,
    };
  }
  return {
    leasing_business_id: agreement.leasing_business_id,
    end_date: agreement.end_date ? moment(agreement.end_date) : null,
    never_ends: !agreement.end_date,
    registration_numbers: (agreement.registration_numbers || []).join('\n'),
    note: agreement.note || '',
    new_attachment: null,
    attachment_name: agreement.attachment_name || '',
    remove_attachment: false,
  };
}

// One vuokra-/leasingsopimus: leasing company's Y-tunnus, end date (or none),
// the cars by registration number and an optional attachment. With no
// `agreement` it is the form for a new one. Saved on its own, apart from the
// organization form around it.
class LeasingAgreementForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = { formData: formDataFrom(props.agreement), deleteModalIsOpen: false };
  }

  componentDidUpdate(prevProps) {
    const { agreement } = this.props;
    // A saved agreement comes back as a new object: show what the server stored.
    if (agreement && agreement !== prevProps.agreement) {
      this.setState({ formData: formDataFrom(agreement) });
    }
  }

  updateFormState = (changedObject) => {
    const { formData } = this.state;
    this.setState({ formData: { ...formData, [changedObject.target]: changedObject.value } });
  };

  changeEndDate = (date) => {
    const { formData } = this.state;
    this.setState({ formData: { ...formData, end_date: date ? moment(date) : null } });
  };

  toggleNeverEnds = () => {
    const { formData } = this.state;
    this.setState({ formData: { ...formData, never_ends: !formData.never_ends } });
  };

  attachmentChanged = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const { formData } = this.state;
      this.setState({
        formData: {
          ...formData, new_attachment: reader.result, attachment_name: file.name, remove_attachment: false,
        },
      });
    };
  };

  removeAttachment = () => {
    const { formData } = this.state;
    this.setState({
      formData: {
        ...formData, new_attachment: null, attachment_name: '', remove_attachment: true,
      },
    });
  };

  getRegistrationNumbers = () => {
    const { formData } = this.state;
    return formData.registration_numbers
      .split(/[\n,;]/)
      .map((value) => value.trim())
      .filter((value) => value);
  };

  isValid = () => {
    const { formData } = this.state;
    return !!formData.leasing_business_id.trim()
      && this.getRegistrationNumbers().length > 0
      && (formData.never_ends || !!formData.end_date);
  };

  submitForm = () => {
    const { onSave } = this.props;
    const { formData } = this.state;
    const data = {
      leasing_business_id: formData.leasing_business_id.trim(),
      end_date: formData.never_ends ? null : formData.end_date.format('YYYY-MM-DD'),
      registration_numbers: this.getRegistrationNumbers(),
      note: formData.note,
    };
    if (formData.new_attachment) {
      data.new_attachment = formData.new_attachment;
      data.attachment_name = formData.attachment_name;
    } else if (formData.remove_attachment) {
      data.remove_attachment = true;
    }
    onSave(data);
  };

  render() {
    const {
      t, agreement, serverErrors, loading, onDelete, onCancel,
    } = this.props;
    const { formData, deleteModalIsOpen } = this.state;
    const hasStoredAttachment = agreement && agreement.attachment && !formData.remove_attachment
      && !formData.new_attachment;

    return (
      <div style={styles.container}>
        {
          agreement && (
            <p style={agreement.is_active ? styles.statusActive : styles.statusEnded}>
              {agreement.is_active ? t('leasingAgreementActive') : t('leasingAgreementEnded')}
            </p>
          )
        }
        <CBInputField
          labelText={t('leasingBusinessIdLabel')}
          name='leasing_business_id'
          onChange={this.updateFormState}
          value={formData.leasing_business_id}
          serverErrors={serverErrors}
          placeholder='1234567-8'
          labelTextStyle={styles.label}
        />
        <p className='font-titillium-web-semi-bold' style={styles.label}>{t('leasingEndDateLabel')}</p>
        {
          !formData.never_ends && (
            <CBDatePicker
              date={formData.end_date}
              changeDate={this.changeEndDate}
              containerStyle={styles.datePicker}
            />
          )
        }
        <CBLabelAndCheckbox
          checked={formData.never_ends}
          labelText={t('leasingNeverEnds')}
          color='white'
          onChange={this.toggleNeverEnds}
        />
        {
          serverErrors && serverErrors.end_date && (
            <p style={styles.error}>{serverErrors.end_date.join(' ')}</p>
          )
        }
        <CBTextAreaField
          labelText={t('leasingRegistrationNumbersLabel')}
          name='registration_numbers'
          onChange={this.updateFormState}
          value={formData.registration_numbers}
          serverErrors={serverErrors}
          placeholder={t('leasingRegistrationNumbersPlaceholder')}
          labelStyle={styles.labelContainer}
          labelTextStyle={styles.label}
        />
        <CBTextAreaField
          labelText={t('leasingNoteLabel')}
          name='note'
          onChange={this.updateFormState}
          value={formData.note}
          serverErrors={serverErrors}
          labelStyle={styles.labelContainer}
          labelTextStyle={styles.label}
          inputStyle={styles.noteInput}
        />
        <p className='font-titillium-web-semi-bold' style={styles.label}>{t('leasingAttachmentLabel')}</p>
        <div style={styles.attachmentRow}>
          {
            hasStoredAttachment && (
              <React.Fragment>
                <a href={agreement.attachment} target='_blank' rel='noopener noreferrer' style={styles.link}>
                  {agreement.attachment_name || t('leasingAttachmentOpen')}
                </a>
                <CBButtonSettings text={t('leasingAttachmentRemove')} onClick={this.removeAttachment} />
              </React.Fragment>
            )
          }
          {
            formData.new_attachment && (
              <p style={styles.attachmentName}>{formData.attachment_name}</p>
            )
          }
          {
            !hasStoredAttachment && (
              <input type='file' onChange={this.attachmentChanged} style={styles.fileInput} />
            )
          }
        </div>
        <div style={styles.buttonRow}>
          <CBButtonSettings
            text={t('leasingSave')}
            disabled={loading || !this.isValid()}
            onClick={this.submitForm}
          />
          {
            agreement ? (
              <CBButtonSettings
                text={t('leasingDelete')}
                disabled={loading}
                onClick={() => this.setState({ deleteModalIsOpen: true })}
              />
            ) : (
              <CBButtonSettings text={t('leasingCancel')} onClick={onCancel} />
            )
          }
        </div>
        {
          agreement && (
            <CBPromptModal
              modalIsOpen={deleteModalIsOpen}
              closeModal={() => this.setState({ deleteModalIsOpen: false })}
              afterOpenModal={() => {}}
              title={t('leasingDeleteTitle')}
              text={t('leasingDeleteText')}
              buttonText={t('leasingDelete')}
              performAction={() => {
                this.setState({ deleteModalIsOpen: false });
                onDelete();
              }}
            />
          )
        }
      </div>
    );
  }
}

const styles = {
  container: {
    position: 'relative',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  label: {
    marginBottom: 5,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    width: '100%',
    color: '#FFFFFF',
  },
  labelContainer: {
    width: '100%',
  },
  statusActive: {
    fontSize: 12,
    color: '#7CD992',
    marginBottom: 8,
  },
  statusEnded: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.6)',
    marginBottom: 8,
  },
  datePicker: {
    width: 150,
    marginBottom: 8,
  },
  noteInput: {
    height: 60,
    width: '100%',
    resize: 'none',
    backgroundColor: 'rgb(235, 235, 235)',
    borderRadius: 16,
    fontSize: 14,
    border: 0,
    padding: 8,
  },
  error: {
    fontSize: 12,
    color: '#FF8A8A',
  },
  attachmentRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  link: {
    color: '#FFFFFF',
    textDecoration: 'underline',
    fontSize: 14,
  },
  attachmentName: {
    fontSize: 14,
  },
  fileInput: {
    color: '#FFFFFF',
    fontSize: 12,
    maxWidth: '100%',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
};

export default translate('SettingsView')(LeasingAgreementForm);
